import type { Locale } from "@/config/locale";

/**
 * Unicode regional indicator sequence (ISO 3166-1 alpha-2), e.g. DE → 🇩🇪.
 * EU uses the EU flag emoji sequence.
 */
export function iso2ToFlagEmoji(code: string): string {
	const upper = code.toUpperCase();
	if (upper === "EU") return "\u{1F1EA}\u{1F1FA}";
	if (upper.length !== 2) return "";
	const base = 0x1f1e6 - 0x41;
	return String.fromCodePoint(upper.charCodeAt(0) + base, upper.charCodeAt(1) + base);
}

/**
 * Primary country/region flag for storefront language.
 *
 * `Record<Locale, string>` is the compile-time forcing function: a new registry
 * locale without an entry here is a type error. Exported so the registry
 * contract can be asserted key-for-key in tests.
 */
export const localeToIso2: Record<Locale, string> = {
	// locale-literals-ok: AGENTS.md — exhaustive Record<Locale, string>; the type system is the gate, so no derivation is needed or wanted.
	de: "DE",
	en: "GB",
	es: "ES",
	tr: "TR",
	fr: "FR",
	it: "IT",
	nl: "NL",
	pl: "PL",
	pt: "PT",
	sv: "SE",
	da: "DK",
	cs: "CZ",
	nb: "NO",
	et: "EE",
	fi: "FI",
	ga: "IE",
	el: "GR",
	hr: "HR",
	lv: "LV",
	mt: "MT",
	ro: "RO",
	sk: "SK",
	sl: "SI",
	hu: "HU",
	// locale-literals-ok: AGENTS.md — bg/lt rows of the exhaustive Record<Locale, …> above; the sweep caps one multi-line map at 24 entries, so the type system stays the gate.
	bg: "BG",
	lt: "LT",
};

export function localeToFlagIso(locale: Locale): string {
	return localeToIso2[locale];
}

export function channelSlugToFlagIso(slug: string): "EU" | "US" {
	const s = slug.toLowerCase();
	if (s.includes("us")) return "US";
	return "EU";
}
